import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import toaster, { Theme } from '../../../../shared/toaster';
import { RootState } from '../../../../store/root-reducer';
import {
  BulkCompaniesGetFilterDto,
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  SavedTabFilters,
} from '../../types';
import {
  exportAllCompaniesAPI,
  exportSelectedCompaniesAPI,
} from '../../helpers/leads.api';
import { dateToString } from '../../../../shared/functions/date';

interface UseCompaniesBulkActionsProps {
  activeLeadType: LeadFinderPrimaryTab;
  activeSubTab: LeadFinderSecondaryTab;
  savedCount?: number;
  selectedCompanies: number[];
  isSelectedAllCompanies: boolean;
  savedFilters: SavedTabFilters;
  searchCompanies?: () => void | Promise<void>;
  fetchLeadTags?: () => void | Promise<void>;
  refreshSavedCounts?: () => void | Promise<void>;
  companiesData?: { profiles: any[] };
  leadTags?: any[];
  setSavedFilters?: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  handleQuery?: (args: {
    filterKey: string;
    filterValue: any;
    filterType: string;
  }) => void;
  setSelectedCompanies?: React.Dispatch<React.SetStateAction<number[]>>;
  setIsSelectedAllCompanies?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useCompaniesBulkActions = ({
  activeLeadType,
  activeSubTab,
  savedCount,
  selectedCompanies,
  isSelectedAllCompanies,
  savedFilters,
  searchCompanies,
  fetchLeadTags,
  refreshSavedCounts,
  companiesData,
  leadTags,
  setSavedFilters,
  handleQuery,
  setSelectedCompanies,
  setIsSelectedAllCompanies,
}: UseCompaniesBulkActionsProps) => {
  const [loadingBulkAction, setLoadingBulkAction] = useState({
    loadingExport: false,
  });
  // User-applied cap on how many records a select-all company action processes.
  const companyMaxLeadsLimit = useSelector(
    (state: RootState) => state.leadFinderV2.selection.companyMaxLeadsLimit,
  );
  const selectedCompanyObjectsByIdRef = useRef<Map<number, any>>(new Map());

  const handleCompanyExport = useCallback(async () => {
    if (activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      return;
    }

    try {
      const isExportAll =
        isSelectedAllCompanies || (selectedCompanies?.length ?? 0) === 0;
      if (
        activeSubTab === LeadFinderSecondaryTab.SAVED &&
        isExportAll &&
        typeof savedCount === 'number' &&
        savedCount === 0
      ) {
        toaster.error(
          'No companies found. Try adjusting your filters and export again.',
          { theme: Theme.New },
        );
        return;
      }

      setLoadingBulkAction((prev) => ({ ...prev, loadingExport: true }));

      let result;

      if (isSelectedAllCompanies || selectedCompanies.length === 0) {
        const companiesFilter: BulkCompaniesGetFilterDto = {};

        if (savedFilters.tags?.length > 0) {
          companiesFilter.tags = savedFilters.tags;
        }

        if (savedFilters.search) {
          companiesFilter.search = savedFilters.search;
        }

        if (savedFilters.createdDate?.length === 2) {
          const [startDate, endDate] = savedFilters.createdDate;
          companiesFilter.createdDate = [
            dateToString(startDate),
            dateToString(endDate),
          ];
        }

        if (savedFilters.listIds?.includes?.length > 0) {
          companiesFilter.listIds = { includes: savedFilters.listIds.includes };
        }
        if (savedFilters.listIds?.excludes?.length > 0) {
          companiesFilter.listIds = {
            ...companiesFilter.listIds,
            excludes: savedFilters.listIds.excludes,
          };
        }
        result = await exportAllCompaniesAPI({
          companiesFilter,
          // ...(companyMaxLeadsLimit != null && {
          //   maxLeadsToBeProcessed: companyMaxLeadsLimit,
          // }),
        });
      } else if (selectedCompanies.length > 0) {
        result = await exportSelectedCompaniesAPI({
          companyIds: selectedCompanies,
        });
      } else {
        toaster.error('No company selected for export', { theme: Theme.New });
        setLoadingBulkAction((prev) => ({ ...prev, loadingExport: false }));
        return;
      }

      const { message, status } = result?.payload || {};

      if (status === 0) {
        toaster.error(message || 'Failed to export companies', {
          theme: Theme.New,
        });
      } else if (status === 2) {
        toaster.warning(
          message || 'Another export action is already in progress.',
          {
            theme: Theme.New,
          },
        );
      } else if (status === 1) {
        toaster.success(
          message ||
            (isSelectedAllCompanies || selectedCompanies.length === 0
              ? 'Export for all saved companies has started.'
              : 'Export for selected companies has started.'),
          {
            theme: Theme.New,
          },
        );
      }
    } catch (error: any) {
      toaster.error(error?.message || 'Error while exporting company data', {
        theme: Theme.New,
      });
    } finally {
      setLoadingBulkAction((prev) => ({ ...prev, loadingExport: false }));
    }
  }, [
    activeLeadType,
    activeSubTab,
    savedCount,
    selectedCompanies,
    isSelectedAllCompanies,
    savedFilters,
    companyMaxLeadsLimit,
  ]);

  // Handle add tag - refresh companies and tags after tag is added (same as people: handleAddTag)
  const handleCompanyAddTag = useCallback(async () => {
    await searchCompanies?.();
    await fetchLeadTags?.();
  }, [searchCompanies, fetchLeadTags]);

  const handleCompanyRemoveTag = useCallback(async () => {
    await searchCompanies?.();
    await refreshSavedCounts?.();
  }, [searchCompanies, refreshSavedCounts]);

  const companyFilter = useMemo(() => {
    const filter: {
      search?: string;
      tagIds?: number[];
      createdDate?: string;
      listIds?: { includes?: number[]; excludes?: number[] };
    } = {};

    if (savedFilters.search) {
      filter.search = savedFilters.search;
    }
    if (savedFilters.tags?.length > 0) {
      filter.tagIds = savedFilters.tags;
    }
    if (savedFilters.createdDate?.length === 2) {
      const [startDate, endDate] = savedFilters.createdDate;
      filter.createdDate = `${dateToString(startDate)},${dateToString(
        endDate,
      )}`;
    }

    const effectiveListIds = savedFilters.listIds;

    if (effectiveListIds?.includes?.length > 0) {
      filter.listIds = {
        ...filter.listIds,
        includes: effectiveListIds.includes,
      };
    }
    if (effectiveListIds?.excludes?.length > 0) {
      filter.listIds = {
        ...filter.listIds,
        excludes: effectiveListIds.excludes,
      };
    }
    return filter;
  }, [savedFilters]);

  useEffect(() => {
    companiesData?.profiles?.forEach((company) => {
      const companyId = company?.id ?? company?.company_id;
      if (companyId != null) {
        selectedCompanyObjectsByIdRef.current.set(Number(companyId), company);
      }
    });
  }, [companiesData?.profiles]);

  const selectedCompanyObjects = useMemo(() => {
    if (!companiesData?.profiles) {
      return [];
    }

    const companiesForTags = isSelectedAllCompanies
      ? companiesData.profiles
      : selectedCompanies
          .map((companyId) =>
            selectedCompanyObjectsByIdRef.current.get(Number(companyId)),
          )
          .filter(Boolean);

    return companiesForTags.map((company) => {
      const companyId = company?.id ?? company?.company_id;
      const tagIds: number[] = [];
      if (company?.tags && Array.isArray(company.tags)) {
        company.tags.forEach((t: any) => {
          const id = t?.id ?? t;
          if (id != null) tagIds.push(Number(id));
        });
      }
      if (
        tagIds.length === 0 &&
        company?.tagsIds &&
        Array.isArray(company.tagsIds)
      ) {
        tagIds.push(...(company.tagsIds as number[]));
      }
      const tags: { id: number; name?: string }[] =
        tagIds.length > 0 && leadTags?.length
          ? tagIds
              .map((tagId) => leadTags.find((t) => t?.id === tagId))
              .filter(Boolean)
              .map((t) => ({ id: t!.id, name: t!.name }))
          : [];
      // TODO: Remove - static tags for remove tag development until API returns tags
      // if (tags.length === 0) {
      //   tags = [
      //     { id: 82688, name: 'test' },
      //     { id: 82699, name: 'test2' },
      //   ];
      // }
      return { ...company, id: companyId, tags };
    });
  }, [
    companiesData?.profiles,
    isSelectedAllCompanies,
    selectedCompanies,
    leadTags,
  ]);

  const companyTagButtonProps = useMemo(() => {
    if (activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      return undefined;
    }
    if (
      !leadTags ||
      !savedFilters ||
      !setSavedFilters ||
      !handleQuery ||
      !setSelectedCompanies
    ) {
      return undefined;
    }
    return {
      handleAddTag: handleCompanyAddTag,
      handleRemoveTag: handleCompanyRemoveTag,
      leadTags,
      selectedCompanies,
      selectedCompanyObjects,
      selectedAllCompanies: isSelectedAllCompanies,
      companyFilter,
      leadsFilter: savedFilters,
      setSavedFilters,
      handleQuery,
      setSelectedCompanies,
      setIsSelectedAllCompanies,
    };
  }, [
    activeLeadType,
    handleCompanyAddTag,
    handleCompanyRemoveTag,
    leadTags,
    selectedCompanies,
    selectedCompanyObjects,
    isSelectedAllCompanies,
    companyFilter,
    savedFilters,
    setSavedFilters,
    handleQuery,
    setSelectedCompanies,
    setIsSelectedAllCompanies,
  ]);

  return {
    loadingBulkAction,
    handleCompanyExport,
    companyTagButtonProps,
  };
};
